import { sourceControlToggleAtom } from '@/atoms/sourceControlAtoms';;
import { healthPathwaysSetupDialogOpenAtom } from '@/atoms/healthPathwaysAtoms';
import { PartnerLogo } from '@heidi/evidence';
import { getPartnerConfig } from '@/components/chat/constants/integrationPartners';
import { Button } from '@heidi/design-system/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { Input } from '@heidi/design-system/input';
import { Link } from '@heidi/design-system/link';
import {
  RadioGroup,
  RadioGroupItem,
} from '@heidi/design-system/radio-group';
import { TypographyP2 } from '@heidi/design-system/typography';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@heidi/design-system/form';
import {
  isHealthPathwaysZoneNotSupportedError,
  useMutateSetupHealthPathways,
} from '@/hooks/mutation/useMutateSetupHealthPathways';
import { useEffectiveEvidenceLocation } from '@/hooks/useEffectiveEvidenceLocation';
import { useHealthPathwaysStatus } from '@/hooks/useHealthPathwaysStatus';
import { useSettingsModal } from '@/features/settings/hooks/useSettingsModal';
import { sortedCountries } from '@heidi/utils/countries';
import {
  HEALTHPATHWAYS_COUNTRIES,
  createHealthPathwaysSetupSchema,
  type HealthPathwaysSetupFormValues,
} from '@/types/healthpathways';
import { IntegrationInfoKey } from '@heidi/schema/ml-scribe';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtom, useSetAtom } from 'jotai';
import { ExternalLink } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

/** Public HealthPathways directory — regional sites (NZ / AU / UK / CA). */
const HEALTHPATHWAYS_LEARN_MORE_URL = 'https://www.healthpathways.info/';

const COUNTRY_LABELS: Record<string, string> = {
  au: 'Australia',
  nz: 'New Zealand',
  gb: 'United Kingdom',
  ca: 'Canada',
};

const HEALTHPATHWAYS_FALLBACK_NAME = 'HealthPathways';

const EVIDENCE_SETTINGS_HREF = '/settings/evidence';

export const HealthPathwaysSetupDialog = () => {
  const intl = useIntl();
  const { open: openSettingsModal } = useSettingsModal();
  const [isOpen, setIsOpen] = useAtom(healthPathwaysSetupDialogOpenAtom);
  const setSourceControlToggle = useSetAtom(sourceControlToggleAtom);
  const { mutateAsync: setup, isPending } = useMutateSetupHealthPathways();
  const { isSetUp, setupConfig } = useHealthPathwaysStatus();

  // EVI-1973: use the shared Evidence-location resolver (evidence country, else
  // account country). Settings already shows the account fallback; the dialog
  // previously read raw evidence_settings.country and blocked fresh CA accounts
  // whose evidence country was never persisted.
  const { country: evidenceCountryName } = useEffectiveEvidenceLocation();

  // EVI-1330: when the dialog is launched from settings against an existing
  // configuration we pre-fill the form so the user can edit postcode / zone
  // type. BE re-uses POST /evidence/healthpathways/setup as an idempotent
  // upsert (replaces zone_id on success, keeps old config on failure).
  const existingPostcode = setupConfig?.postcode ?? '';
  const existingZoneType: HealthPathwaysSetupFormValues['zone_type'] =
    setupConfig?.zone_type ?? 'community';

  // EVI-1330: derive country from the effective Evidence country instead of
  // asking them to pick one. If it isn't HP-supported we block setup.
  const detectedCountryCode = useMemo(() => {
    if (!evidenceCountryName) return '';
    const match = sortedCountries.find((c) => c.name === evidenceCountryName);
    const code = match?.countryCode?.toLowerCase() ?? '';
    return (HEALTHPATHWAYS_COUNTRIES as readonly string[]).includes(code)
      ? code
      : '';
  }, [evidenceCountryName]);

  const isCountrySupported = detectedCountryCode !== '';
  const detectedCountryLabel =
    COUNTRY_LABELS[detectedCountryCode] ?? evidenceCountryName ?? '';

  const partner = useMemo(
    () => getPartnerConfig(IntegrationInfoKey.healthpathways),
    []
  );
  const displayName = partner?.displayName ?? HEALTHPATHWAYS_FALLBACK_NAME;

  const schema = useMemo(
    () =>
      createHealthPathwaysSetupSchema({
        countryRequired: intl.formatMessage({
          defaultMessage: 'Country is required',
          id: 'integrationPartners.healthpathways.countryRequired',
        }),
        postcodeRequired: intl.formatMessage({
          defaultMessage: 'Postcode is required',
          id: 'integrationPartners.healthpathways.postcodeRequired',
        }),
        postcodeInvalidFormat: intl.formatMessage({
          defaultMessage: "That doesn't look like a valid postcode for your country.",
          id: 'integrationPartners.healthpathways.postcodeInvalidFormat',
        }),
      }),
    [intl]
  );

  const form = useForm<HealthPathwaysSetupFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      country_code: detectedCountryCode,
      postcode: existingPostcode,
      zone_type: existingZoneType,
    },
  });

  const [zoneUnsupported, setZoneUnsupported] = useState(false);
  const livePostcode = form.watch('postcode');
  const watchedZoneType = form.watch('zone_type');
  useEffect(() => {
    setZoneUnsupported(false);
  }, [livePostcode, watchedZoneType]);

  // Re-sync the form whenever the dialog opens so re-opens reflect the latest
  // setup_config (e.g., user edits then re-opens, or first-time setup after
  // an earlier dismiss).
  useEffect(() => {
    if (!isOpen) return;
    form.reset({
      country_code: detectedCountryCode,
      postcode: existingPostcode,
      zone_type: existingZoneType,
    });
    // Watch clear effect doesn't fire on reopen if form values are unchanged.
    setZoneUnsupported(false);
  }, [isOpen, detectedCountryCode, existingPostcode, existingZoneType, form]);

  const onSubmit = async (values: HealthPathwaysSetupFormValues) => {
    if (!isCountrySupported) return;
    setZoneUnsupported(false);
    const isEditing = isSetUp;
    try {
      await setup({
        country_code: detectedCountryCode,
        postcode: values.postcode.trim(),
        zone_type: values.zone_type,
      });
      // Only auto-enable the integration on first setup — editing an existing
      // zone shouldn't flip the chat toggle if the user had disabled it.
      if (!isEditing) {
        setSourceControlToggle((prev) => ({
          ...prev,
          integrationPartners: {
            ...prev.integrationPartners,
            [IntegrationInfoKey.healthpathways]: true,
          },
        }));
      }
      form.reset();
      setIsOpen(false);
    } catch (error) {
      if (isHealthPathwaysZoneNotSupportedError(error)) {
        setZoneUnsupported(true);
      }
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) form.reset();
    setIsOpen(open);
  };

  // EVI-1330: closes the HP dialog and jumps the user straight into evidence
  // settings (where the country picker lives) so they can fix the missing /
  // unsupported account country without hunting for the right screen.
  const handleOpenEvidenceSettings = () => {
    handleOpenChange(false);
    openSettingsModal(EVIDENCE_SETTINGS_HREF, 'healthpathways_setup_dialog');
  };

  const postcodePlaceholder = intl.formatMessage(
    {
      defaultMessage: 'Enter your {country} postcode',
      id: 'integrationPartners.healthpathways.postcodePlaceholder',
    },
    { country: detectedCountryLabel }
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle asChild>
            <div className="flex items-center gap-2">
              {partner ? (
                <PartnerLogo
                  src={partner.logo}
                  brandColor={partner.brandColor}
                  size={28}
                  alt={displayName}
                />
              ) : null}
              <span>{displayName}</span>
            </div>
          </DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-col gap-1">
              <span>
                <FormattedMessage
                  defaultMessage="HealthPathways provides locally agreed care pathways."
                  id="integrationPartners.healthpathways.description"
                />
              </span>
              <Link
                href={HEALTHPATHWAYS_LEARN_MORE_URL}
                target="_blank"
                rel="noreferrer"
                size="xs"
                className="inline-flex w-fit items-center gap-1 hover:underline"
              >
                <ExternalLink size={12} />
                <FormattedMessage
                  defaultMessage="Learn more"
                  id="integrationPartners.healthpathways.learnMore"
                />
              </Link>
            </div>
          </DialogDescription>
        </DialogHeader>

        {!isCountrySupported ? (
          <>
            <TypographyP2 className="text-validation-destructive">
              <FormattedMessage
                defaultMessage="HealthPathways isn't available for your account country. Set your evidence country to Australia, New Zealand, United Kingdom, or Canada to continue."
                id="integrationPartners.healthpathways.countryUnsupported"
              />
            </TypographyP2>
            <DialogFooter>
              <Button size="lg"
                type="button"
                variant="ghost"
                onClick={() => handleOpenChange(false)}
              >
                <FormattedMessage defaultMessage="Close" id="common.close" />
              </Button>
              <Button size="lg"
                type="button"
                onClick={handleOpenEvidenceSettings}
                data-testid="open-evidence-settings"
              >
                <FormattedMessage
                  defaultMessage="Open evidence settings"
                  id="integrationPartners.healthpathways.openEvidenceSettings"
                />
              </Button>
            </DialogFooter>
          </>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
            >
              <FormField
                control={form.control}
                name="postcode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <FormattedMessage
                        defaultMessage="Postcode you practice in"
                        id="integrationPartners.healthpathways.postcodeLabel"
                      />
                    </FormLabel>
                    <FormControl>
                      <Input placeholder={postcodePlaceholder} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="zone_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <FormattedMessage
                        defaultMessage="Your practice setting"
                        id="integrationPartners.healthpathways.practiceSettingLabel"
                      />
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="gap-3"
                      >
                        <label
                          htmlFor="hp-zone-community"
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <RadioGroupItem
                            value="community"
                            id="hp-zone-community"
                          />
                          <TypographyP2>
                            <FormattedMessage
                              defaultMessage="<b>Community</b> — For primary care clinicians"
                              id="integrationPartners.healthpathways.zoneCommunityLabel"
                              values={{
                                b: (chunks) => <strong>{chunks}</strong>,
                              }}
                            />
                          </TypographyP2>
                        </label>
                        <label
                          htmlFor="hp-zone-hospital"
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <RadioGroupItem
                            value="hospital"
                            id="hp-zone-hospital"
                          />
                          <TypographyP2>
                            <FormattedMessage
                              defaultMessage="<b>Hospital</b> — For hospital clinicians"
                              id="integrationPartners.healthpathways.zoneHospitalLabel"
                              values={{
                                b: (chunks) => <strong>{chunks}</strong>,
                              }}
                            />
                          </TypographyP2>
                        </label>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {zoneUnsupported ? (
                <TypographyP2
                  role="alert"
                  className="text-validation-destructive"
                  data-testid="hp-zone-unsupported"
                >
                  <FormattedMessage
                    defaultMessage="HealthPathways isn't available for this postcode and practice setting yet, so it can't be set up for this region right now."
                    id="integrationPartners.healthpathways.zoneUnsupported"
                  />
                </TypographyP2>
              ) : null}

              <DialogFooter>
                <Button size="lg"
                  type="button"
                  variant="ghost"
                  onClick={() => handleOpenChange(false)}
                  disabled={isPending}
                >
                  <FormattedMessage defaultMessage="Cancel" id="common.cancel" />
                </Button>
                <Button size="lg" type="submit" disabled={isPending}>
                  {isSetUp ? (
                    <FormattedMessage
                      defaultMessage="Save changes"
                      id="integrationPartners.healthpathways.saveChanges"
                    />
                  ) : (
                    <FormattedMessage
                      defaultMessage="Connect"
                      id="integrationPartners.healthpathways.connect"
                    />
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
